import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import * as types from '../mutation-types';
import { INBOX_TYPES } from 'shared/mixins/inboxMixin';
import { TEMPLATE_MEDIA_FORMATS } from 'shared/constants/messages';
import InboxesAPI from '../../api/inboxes';
import WebChannel from '../../api/channel/webChannel';
import FBChannel from '../../api/channel/fbChannel';
import EvolutionChannel from '../../api/channel/evolutionChannel';
import TwilioChannel from '../../api/channel/twilioChannel';
import WhatsappAuthorization from '../../api/whatsappAuthorization';
import { throwErrorMessage } from '../utils/api';
import AnalyticsHelper from '../../helper/AnalyticsHelper';
import { ACCOUNT_EVENTS } from '../../helper/AnalyticsHelper/events';
import Vue from 'vue';

const buildInboxData = inboxParams => {
  const formData = new FormData();
  const { channel = {}, ...inboxProperties } = inboxParams;
  Object.keys(inboxProperties).forEach(key => {
    formData.append(key, inboxProperties[key]);
  });
  const { selectedFeatureFlags, ...channelParams } = channel;
  // selectedFeatureFlags needs to be empty when creating a website channel
  if (selectedFeatureFlags) {
    if (selectedFeatureFlags.length) {
      selectedFeatureFlags.forEach(featureFlag => {
        formData.append(`channel[selected_feature_flags][]`, featureFlag);
      });
    } else {
      formData.append('channel[selected_feature_flags][]', '');
    }
  }
  Object.keys(channelParams).forEach(key => {
    formData.append(`channel[${key}]`, channel[key]);
  });
  return formData;
};

export const state = {
  records: [],
  uiFlags: {
    isFetching: false,
    isFetchingItem: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
    isUpdatingIMAP: false,
    isUpdatingSMTP: false,
  },
  EVOLUTION_API_CONNECT: {},
};

export const getters = {
  getInboxes($state) {
    return $state.records;
  },
  getWhatsAppTemplates: $state => inboxId => {
    // Se excluyen solo los inboxes de Facebook (que ahora tienen su propia columna
    // message_templates para Messenger, vía getMessengerTemplates); el resto de canales
    // —WhatsApp y los inboxes API que usan el fallback additional_attributes.message_templates—
    // se mantienen como antes para no regresar [] indebidamente.
    const [inbox] = $state.records.filter(
      record =>
        record.id === Number(inboxId) && record.channel_type !== INBOX_TYPES.FB
    );

    const {
      message_templates: whatsAppMessageTemplates,
      additional_attributes: additionalAttributes,
    } = inbox || {};

    const { message_templates: apiInboxMessageTemplates } =
      additionalAttributes || {};
    const messagesTemplates =
      whatsAppMessageTemplates || apiInboxMessageTemplates;

    // Solo se exponen las plantillas aprobadas por Meta y sin componentes de media no soportados.
    if (messagesTemplates instanceof Array) {
      return messagesTemplates
        .filter(template => template.status?.toLowerCase() === 'approved')
        .filter(template => {
          return !template.components?.some(i =>
            TEMPLATE_MEDIA_FORMATS.includes(i.format)
          );
        });
    }
    return [];
  },
  getMessengerTemplates: $state => inboxId => {
    const [inbox] = $state.records.filter(
      record =>
        record.id === Number(inboxId) && record.channel_type === INBOX_TYPES.FB
    );

    const {
      message_templates: messengerMessageTemplates,
      additional_attributes: additionalAttributes,
    } = inbox || {};

    const { message_templates: apiInboxMessageTemplates } =
      additionalAttributes || {};
    const messagesTemplates =
      messengerMessageTemplates || apiInboxMessageTemplates;

    // filtering out the messenger templates with media
    if (messagesTemplates instanceof Array) {
      return messagesTemplates.filter(template => {
        return !template.components?.some(i =>
          TEMPLATE_MEDIA_FORMATS.includes(i.format)
        );
      });
    }
    return [];
  },
  getNewConversationInboxes($state) {
    return $state.records.filter(inbox => {
      const {
        channel_type: channelType,
        phone_number: phoneNumber = '',
      } = inbox;

      const isEmailChannel = channelType === INBOX_TYPES.EMAIL;
      const isSmsChannel =
        channelType === INBOX_TYPES.TWILIO &&
        phoneNumber.startsWith('whatsapp');
      return isEmailChannel || isSmsChannel;
    });
  },
  getInbox: $state => inboxId => {
    const [inbox] = $state.records.filter(
      record => record.id === Number(inboxId)
    );
    return inbox || {};
  },
  getUIFlags($state) {
    return $state.uiFlags;
  },
  getWebsiteInboxes($state) {
    return $state.records.filter(item => item.channel_type === INBOX_TYPES.WEB);
  },
  getTwilioInboxes($state) {
    return $state.records.filter(
      item => item.channel_type === INBOX_TYPES.TWILIO
    );
  },
  getSMSInboxes($state) {
    return $state.records.filter(
      item =>
        item.channel_type === INBOX_TYPES.SMS ||
        (item.channel_type === INBOX_TYPES.TWILIO && item.medium === 'sms')
    );
  },
  dialogFlowEnabledInboxes($state) {
    return $state.records.filter(
      item => item.channel_type !== INBOX_TYPES.EMAIL
    );
  },
};

const sendAnalyticsEvent = channelType => {
  AnalyticsHelper.track(ACCOUNT_EVENTS.ADDED_AN_INBOX, {
    channelType,
  });
};

export const actions = {
  get: async ({ commit }) => {
    commit(types.default.SET_INBOXES_UI_FLAG, { isFetching: true });
    try {
      const response = await InboxesAPI.get();
      commit(types.default.SET_INBOXES_UI_FLAG, { isFetching: false });
      commit(types.default.SET_INBOXES, response.data.payload);
    } catch (error) {
      commit(types.default.SET_INBOXES_UI_FLAG, { isFetching: false });
    }
  },
  // Refresca un solo inbox (no todos). Se usa como fallback al abrir una conversación de
  // Facebook cuyas plantillas aún no están en el store (el broadcast `inbox.templates_updated`
  // pudo perderse si se emitió antes de que el cliente se suscribiera). Evita el GET /inboxes global.
  fetchInbox: async ({ commit }, inboxId) => {
    try {
      const response = await InboxesAPI.show(inboxId);
      // UPDATE_INBOX (updateAttributes) mergea con Vue.set => reactivo (a diferencia de
      // SET_INBOXES_ITEM, que asigna por índice y no dispara reactividad en Vue 2). Así el
      // getter getMessengerTemplates recomputa y el botón aparece. `show` trae el inbox completo.
      commit(types.default.UPDATE_INBOX, response.data);
    } catch (error) {
      // No se propaga: es un refresco oportunista y un fallo no debe romper la vista de chat.
      // Se loguea (sin mostrar al usuario) para poder diagnosticar problemas de auth/red.
      console.error('Error refreshing inbox templates:', error);
    }
  },
  createChannel: async ({ commit }, params) => {
    try {
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: true });
      const response = await WebChannel.create(params);
      commit(types.default.ADD_INBOXES, response.data);
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: false });
      const { channel = {} } = params;
      sendAnalyticsEvent(channel.type);
      return response.data;
    } catch (error) {
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: false });
      throw new Error(error);
    }
  },
  // Alta por Embedded Signup: el popup de Meta ya devolvió code + waba_id + phone_number_id.
  // Sin banderas de UI: el componente lleva su propio estado de "conectando", igual que la reconexión.
  createWhatsAppEmbeddedSignup: async ({ commit }, params) => {
    const response = await WhatsappAuthorization.create(params);
    commit(types.default.ADD_INBOXES, response.data);
    return response.data;
  },
  createWebsiteChannel: async ({ commit }, params) => {
    try {
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: true });
      const response = await WebChannel.create(buildInboxData(params));
      commit(types.default.ADD_INBOXES, response.data);
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: false });
      sendAnalyticsEvent('website');
      return response.data;
    } catch (error) {
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: false });
      return throwErrorMessage(error);
    }
  },
  createTwilioChannel: async ({ commit }, params) => {
    try {
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: true });
      const response = await TwilioChannel.create(params);
      commit(types.default.ADD_INBOXES, response.data);
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: false });
      sendAnalyticsEvent('twilio');
      return response.data;
    } catch (error) {
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: false });
      throw new Error(error);
    }
  },
  // Se conecta una instancia de Evolution API y actualiza el estado de la bandeja de entrada
  connectEvolutionInstance: async ({ commit }, params) => {
    try {
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: true });
      const response = await EvolutionChannel.connectEvolutionInstance(params);
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: false });
      return response.data;
    } catch (error) {
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: false });
      throw new Error(error);
    }
  },
  // Crea una instancia de Evolution API y actualiza el estado de la bandeja de entrada
  createEvolutionInstance: async ({ commit }, params) => {
    try {
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: true });
      const response = await EvolutionChannel.createEvolutionInstance(params);
      commit(types.default.ADD_INBOXES, response.data?.inbox);
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: false });
      return response.data;
    } catch (error) {
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: false });
      throw new Error(error);
    }
  },
  // Desvincula una instancia de Evolution API y actualiza el estado de la bandeja de entrada
  unlinkEvolutionInstance: async ({ commit }, params) => {
    try {
      commit(types.default.SET_INBOXES_UI_FLAG, { isUpdating: true });
      const response = await EvolutionChannel.unlinkEvolutionInstance(params);
      commit(types.default.EDIT_INBOXES, response.data?.inbox);
      commit(types.default.SET_INBOXES_UI_FLAG, { isUpdating: false });
      return response.data;
    } catch (error) {
      commit(types.default.SET_INBOXES_UI_FLAG, { isUpdating: false });
      throw new Error(error);
    }
  },
  createFBChannel: async ({ commit }, params) => {
    try {
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: true });
      const response = await FBChannel.create(params);
      commit(types.default.ADD_INBOXES, response.data);
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: false });
      sendAnalyticsEvent('facebook');
      return response.data;
    } catch (error) {
      commit(types.default.SET_INBOXES_UI_FLAG, { isCreating: false });
      throw new Error(error);
    }
  },
  updateInbox: async ({ commit }, { id, formData = true, ...inboxParams }) => {
    commit(types.default.SET_INBOXES_UI_FLAG, { isUpdating: true });
    try {
      const response = await InboxesAPI.update(
        id,
        formData ? buildInboxData(inboxParams) : inboxParams
      );
      commit(types.default.EDIT_INBOXES, response.data);
      commit(types.default.SET_INBOXES_UI_FLAG, { isUpdating: false });
    } catch (error) {
      commit(types.default.SET_INBOXES_UI_FLAG, { isUpdating: false });
      throwErrorMessage(error);
    }
  },
  updateInboxIMAP: async ({ commit }, { id, ...inboxParams }) => {
    commit(types.default.SET_INBOXES_UI_FLAG, { isUpdatingIMAP: true });
    try {
      const response = await InboxesAPI.update(id, inboxParams);
      commit(types.default.EDIT_INBOXES, response.data);
      commit(types.default.SET_INBOXES_UI_FLAG, { isUpdatingIMAP: false });
    } catch (error) {
      commit(types.default.SET_INBOXES_UI_FLAG, { isUpdatingIMAP: false });
      throwErrorMessage(error);
    }
  },
  updateInboxSMTP: async ({ commit }, { id, ...inboxParams }) => {
    commit(types.default.SET_INBOXES_UI_FLAG, { isUpdatingSMTP: true });
    try {
      const response = await InboxesAPI.update(id, inboxParams);
      commit(types.default.EDIT_INBOXES, response.data);
      commit(types.default.SET_INBOXES_UI_FLAG, { isUpdatingSMTP: false });
    } catch (error) {
      commit(types.default.SET_INBOXES_UI_FLAG, { isUpdatingSMTP: false });
      throwErrorMessage(error);
    }
  },
  delete: async ({ commit }, inboxId) => {
    commit(types.default.SET_INBOXES_UI_FLAG, { isDeleting: true });
    try {
      await InboxesAPI.delete(inboxId);
      commit(types.default.DELETE_INBOXES, inboxId);
      commit(types.default.SET_INBOXES_UI_FLAG, { isDeleting: false });
    } catch (error) {
      commit(types.default.SET_INBOXES_UI_FLAG, { isDeleting: false });
      throw error;
    }
  },
  // Reconexión por Embedded Signup: mismo endpoint del alta, con inbox_id; devuelve el inbox actualizado.
  reauthorizeWhatsAppEmbeddedSignup: async ({ commit }, params) => {
    const response = await WhatsappAuthorization.create(params);
    commit(types.default.EDIT_INBOXES, response.data);
    return response.data;
  },
  reauthorizeFacebookPage: async ({ commit }, params) => {
    try {
      const response = await FBChannel.reauthorizeFacebookPage(params);
      commit(types.default.EDIT_INBOXES, response.data);
    } catch (error) {
      throw new Error(error.message);
    }
  },
  deleteInboxAvatar: async (_, inboxId) => {
    try {
      await InboxesAPI.deleteInboxAvatar(inboxId);
    } catch (error) {
      throw new Error(error);
    }
  },
  /**
   * Comprobamos conexion del conmutador
   */
  checkConnection: async (_, data) => {
    try {
      return await InboxesAPI.checkConnection(data);
    } catch (error) {
      throw new Error(error);
    }
  },
  // Maneja la conexión con Evolution API y actualiza el estado global
  evolutionApiConnect({ commit }, data) {
    commit(types.default.EVOLUTION_API_CONNECT, data);
  },
  // Maneja la conexion y desconexión de Evolution API y actualiza el estado global
  evolutionApiLinkedChange({ commit }, data) {
    data.inbox.linked = data.linked;
    data.inbox.allow_send_messages = data.allow_send_messages;
    commit(types.default.EVOLUTION_API_CHANGED, data.inbox);
  },
  // Actualiza (merge) los atributos de un inbox en el store desde un evento en tiempo real,
  // p. ej. message_templates tras sincronizarse. Espera { id, ...attrs }.
  updateInboxAttributes({ commit }, data) {
    commit(types.default.UPDATE_INBOX, data);
  },
};

export const mutations = {
  [types.default.SET_INBOXES_UI_FLAG]($state, uiFlag) {
    $state.uiFlags = { ...$state.uiFlags, ...uiFlag };
  },
  [types.default.SET_INBOXES]: MutationHelpers.set,
  [types.default.SET_INBOXES_ITEM]: MutationHelpers.setSingleRecord,
  [types.default.ADD_INBOXES]: MutationHelpers.create,
  [types.default.EDIT_INBOXES]: MutationHelpers.update,
  [types.default.DELETE_INBOXES]: MutationHelpers.destroy,
  [types.default.EVOLUTION_API_CONNECT]($state, data) {
    Vue.set($state, 'EVOLUTION_API_CONNECT', data);
  },
  [types.default.EVOLUTION_API_CHANGED]: MutationHelpers.updateAttributes,
  [types.default.UPDATE_INBOX]: MutationHelpers.updateAttributes,
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
